import * as React from "react";
import * as ReactDOM from "react-dom";
import { Route } from "react-router-dom";
import * as models from "../../../models";
import * as modelsGet from "../../../models/get";
import * as modelsPost from "../../../models/post";
import { IComboBoxValue } from "../../../models/form";
import * as globals from "../../../globals";
import * as utils from "../../../utils/utils";
import * as apiCalendars from "../../../api/calendars";
import * as apiCalendarGroups from "../../../api/calendarGroups";
import * as apiCementeries from "../../../api/cementeries";
import * as apiTimes from "../../../api/times";
import { Window } from "../../../components/window";
import { Loader, LoaderStyle } from "../../../components/loader";
import { Button, ButtonType } from "../../../components/button";
import { ScrollViewer, ScrollBarVisibility } from "../../../components/scrollViewer";
import { FormGroup } from "../../../components/form";
import { ComboBox, ComboBoxType, DROPDOWN_TEXT_ITEM_HEIGHT, DropDownTextItem } from "../../../components/comboBox";
import { InputMask, InputSize } from "../../../components/input";
import { InputValueType } from "../../../enumerations";
import { TextBox } from "../../../components/textBox";
import { Alert, AlertType } from "../../../components/alert";
import ColorPicker from "../../../components/colorpicker";
import { CALENDAR_COLORS } from "../../../utils/constants";
import { CheckBox } from "../../../components/checkbox";
import { GroupBox } from "../../../components/groupBox";
import { TextArea } from "../../../components/textArea";
import { Time } from "../../../types/time";

enum Mode
{
    create,
    edit
}

interface IProperties
{
    /**
     * Die ID des zu öffnenden Datensatzes. (Ist leer wenn eine neuer Datensatz erstellt werden soll.)
     */
    calendarId?: number,

    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (refresh?: boolean) => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Gibt den Modus an in dem sich der Dialog befindet.
     */
    mode?: Mode,

    /**
     * Wenn im Bearbeitungsmodus, dann ist diese Eigenschaft gefüllt.
     */
    calendar?: modelsGet.ICalendar,

    calendarGroups?: modelsGet.ICalendarGroup[],
    cementeries?: modelsGet.ICementery[],
    rangeFrom?: modelsGet.ITime[],
    rangeTo?: modelsGet.ITime[],
    weekDays?: models.IWeekDay[],
    lockFrom?: modelsGet.ITime[],
    lockTo?: modelsGet.ITime[],

    /**
     * Enthält die Eingabedaten des Forms.
     */
    form?: {
        calendarname?: string,
        color?: models.IColor,
        calendar_group?: IComboBoxValue<string, number>,
        cementery?: IComboBoxValue<string, number>,
        timescale_view?: number,
        range_from?: IComboBoxValue<string, string>,
        range_to?: IComboBoxValue<string, string>,
        default_interval?: number,
        show_free_blocks?: boolean,
        complete_appointment?: boolean,
        order_nr?: number,
        automatic_setuptime?: boolean,
        setuptime?: number,
        lock_weekday?: IComboBoxValue<string, string>,
        lock_from?: IComboBoxValue<string, string>,
        lock_to?: IComboBoxValue<string, string>,
        locks?: models.ILock[],
        information?: string
    },

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export default class Dialog extends React.Component<IProperties, IState>
{
    private _initialState: IState;
    private _isSaveButtonDisabled: boolean;

    /**
     * Erstellt eine neue Instanz der Klasse @see Dialog.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        // Modus ermitteln.
        var mode: Mode = Mode.create;
        if (props.calendarId > 0)
        {
            mode = Mode.edit;
        }

        this.state = {
            isLoading: true,
            mode: mode,
            form: {
                calendar_group: {},
                cementery: {},
                range_from: {},
                range_to: {},
                lock_weekday: {},
                lock_from: {},
                lock_to: {},
                locks: []
            },
            errors: {}
        };

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        if (this.state.mode === Mode.edit)
        {
            await this.initEditModeAsync(props);
        }
        else if (this.state.mode === Mode.create)
        {
            await this.initCreateModeAsync(props);
        }
    }

    /**
     * Initialisert den Edit Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initEditModeAsync(props: IProperties)
    {
        // Trauerhalle aus der Datenbank laden.
        var calendar: modelsGet.ICalendar = await apiCalendars.getCalendar(props.calendarId);
        var settings: modelsGet.ICalendarSettings = JSON.parse(calendar.settings);

        var calendarGroups: modelsGet.ICalendarGroup[] = await apiCalendarGroups.getCalendarGroups();
        var cementeries: modelsGet.ICementery[] = await apiCementeries.getCementeries();
        var rangeFrom: modelsGet.ITime[] = await apiTimes.getCompleteTime();
        var rangeTo: modelsGet.ITime[] = await apiTimes.getCompleteTime(settings.time.range[0] + ":00");
        var weekDays: models.IWeekDay[] = globals.getWeekDays();
        var lockFrom: modelsGet.ITime[] = rangeFrom;
        var lockTo: modelsGet.ITime[] = rangeFrom;

        // Werte setzen.
        this.setState((state: IState) =>
        {
            state.isLoading = false;
            state.errors = {};
            state.alertMessage = null;
            state.alertType = null;
            state.calendar = calendar;
            state.calendarGroups = calendarGroups;
            state.cementeries = cementeries;
            state.rangeFrom = rangeFrom;
            state.rangeTo = rangeTo;
            state.weekDays = weekDays;
            state.lockFrom = lockFrom;
            state.lockTo = lockTo;

            state.form.calendarname = calendar.name;
            state.form.color = CALENDAR_COLORS.where(c => c.color === calendar.color).first();

            state.form.calendar_group = {
                value: calendar.group.group_name,
                selectedValue: calendar.group.id
            };

            if (calendar.cementery != null)
            {
                state.form.cementery = {
                    value: calendar.cementery.name_long,
                    selectedValue: calendar.cementery.id
                };
            }

            state.form.timescale_view = settings.time.scale;

            state.form.range_from = {
                value: settings.time.range[0],
                selectedValue: settings.time.range[0]
            };

            state.form.range_to = {
                value: settings.time.range[1],
                selectedValue: settings.time.range[1]
            };

            state.form.default_interval = settings.time.default_interval;
            state.form.show_free_blocks = settings.time.show_free_blocks;
            state.form.complete_appointment = settings.time.complete_appointment;
            state.form.order_nr = calendar.order_nr;
            state.form.automatic_setuptime = settings.time.automatic_setuptime;
            state.form.setuptime = settings.time.setuptime;
            state.form.locks = settings.time.locks || [];
            state.form.information = calendar.information;

            return state;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Initialisert den Create Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initCreateModeAsync(props: IProperties)
    {
        var calendarGroups: modelsGet.ICalendarGroup[] = await apiCalendarGroups.getCalendarGroups();
        var cementeries: modelsGet.ICementery[] = await apiCementeries.getCementeries();
        var rangeFrom: modelsGet.ITime[] = await apiTimes.getCompleteTime();
        var rangeTo: modelsGet.ITime[] = await apiTimes.getCompleteTime("08:00:00");
        var weekDays: models.IWeekDay[] = globals.getWeekDays();
        var lockFrom: modelsGet.ITime[] = rangeFrom;
        var lockTo: modelsGet.ITime[] = rangeFrom;

        this.setState(() =>
        {
            const newState: IState = {
                form: {
                    calendar_group: {},
                    cementery: {},
                    range_from: {},
                    range_to: {},
                    lock_weekday: {},
                    lock_from: {},
                    lock_to: {},
                    locks: []
                }
            };
            newState.isLoading = false;
            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;
            newState.calendarGroups = calendarGroups;
            newState.cementeries = cementeries;
            newState.rangeFrom = rangeFrom;
            newState.rangeTo = rangeTo;
            newState.weekDays = weekDays;
            newState.lockFrom = lockFrom;
            newState.lockTo = lockTo;

            newState.form.color = CALENDAR_COLORS.where(c => c.name === "null").first();

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Wird ausgeführt, bevor gerendert wird.
     * Hier wird geprüft ob der Button zum erstellen oder speichern
     * freigschaltet werden soll.
     * @param nextProps Die neuen Eigenschaften.
     * @param nextState Der neue State.
     */
    componentWillUpdate(nextProps: IProperties, nextState: IState)
    {
        this._isSaveButtonDisabled = true;

        if (this._initialState != null)
        {
            const { form } = this._initialState;

            if (form.calendarname !== nextState.form.calendarname)
                this._isSaveButtonDisabled = false;

            if (form.color.name !== nextState.form.color.name)
                this._isSaveButtonDisabled = false;

            if (form.calendar_group.selectedValue !== nextState.form.calendar_group.selectedValue)
                this._isSaveButtonDisabled = false;

            if (form.cementery.selectedValue !== nextState.form.cementery.selectedValue)
                this._isSaveButtonDisabled = false;

            if (form.order_nr !== nextState.form.order_nr)
                this._isSaveButtonDisabled = false;

            if (form.information !== nextState.form.information)
                this._isSaveButtonDisabled = false;

            if (form.timescale_view !== nextState.form.timescale_view)
                this._isSaveButtonDisabled = false;

            if (form.range_from.selectedValue !== nextState.form.range_from.selectedValue)
                this._isSaveButtonDisabled = false;

            if (form.range_to.selectedValue !== nextState.form.range_to.selectedValue)
                this._isSaveButtonDisabled = false;

            if (form.default_interval !== nextState.form.default_interval)
                this._isSaveButtonDisabled = false;

            if (form.show_free_blocks !== nextState.form.show_free_blocks)
                this._isSaveButtonDisabled = false;

            if (form.complete_appointment !== nextState.form.complete_appointment)
                this._isSaveButtonDisabled = false;

            if (form.automatic_setuptime !== nextState.form.automatic_setuptime)
                this._isSaveButtonDisabled = false;

            if (form.setuptime !== nextState.form.setuptime)
                this._isSaveButtonDisabled = false;

            if (form.setuptime !== nextState.form.setuptime)
                this._isSaveButtonDisabled = false;

            if (form.locks.length !== nextState.form.locks.length)
            {
                this._isSaveButtonDisabled = false;
            }
            else if (form.locks.length > 0 && nextState.form.locks.length > 0)
            {
                for (let lock of nextState.form.locks)
                {
                    let from: Time = Time.parse(lock.from);
                    let to: Time = Time.parse(lock.to);

                    if (!form.locks.any(l => l.weekday === lock.weekday && l.from === from.toString() && l.to === to.toString()))
                    {
                        this._isSaveButtonDisabled = false;
                        break;
                    }
                }
            }
        }
    }

    /**
     * Wird ausgeführt, bevor der Dialog sich schließt.
     */
    private onClosing = (refresh: boolean = false) =>
    {
        var closeDialog: boolean = true;

        if (!this._isSaveButtonDisabled)
            closeDialog = confirm("Es sind noch ungespeicherte Änderungen vorhanden. Wollen Sie den Dialog trotzdem schließen?");

        if (closeDialog)
        {
            const { onClose } = this.props;
            onClose && onClose(refresh);
            return true;
        }
    }

    /**
     * Ändert den geöffneten Datensatz oder erstellt einen neuen.
     */
    private onSaveOrCreateClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { mode, calendar, form } = this.state;

            // Neues POST Objekt erstellen.
            var newData: modelsPost.ICalendar = {};
            newData.calendarname = form.calendarname;
            newData.color = form.color.color;
            newData.calendar_group_id = form.calendar_group.selectedValue;
            newData.cementery_id = form.cementery.selectedValue;
            newData.order_nr = form.order_nr;
            newData.information = form.information;

            newData.timescale_view = form.timescale_view;
            newData.range_from = form.range_from.selectedValue;
            newData.range_to = form.range_to.selectedValue;
            newData.default_interval = form.default_interval;
            newData.show_free_blocks = form.show_free_blocks;
            newData.complete_appointment = form.complete_appointment;

            newData.automatic_setuptime = form.automatic_setuptime;
            newData.setuptime = form.setuptime;

            newData.locks = form.locks;

            if (mode === Mode.edit)
            {
                await apiCalendars.put(calendar.id, newData);
            }
            else
            {
                await apiCalendars.post(newData);
            }

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Löscht den geöffneten Datensatz.
     */
    private onDeleteClick = async () =>
    {
        var deleteData = confirm("Wollen Sie den Datensatz wirklich löschen?");
        if (!deleteData)
            return;

        try
        {
            this.setState({
                isLoading: true
            });

            const { calendar } = this.state;

            await apiCalendars.remove(calendar.id);

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Wird ausgeführt, sobald sich der Wert eines Controls ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     * @param name Der Name des Form Elements.
     */
    private onControlTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0];
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald auf eine Farbe im ColorPicker geklickt wird.
     * @param color Die selektierte Farbe.
     */
    private onColorChanged = (color: models.IColor) =>
    {
        this.setState((state: IState) =>
        {
            state.form.color = { ...color };
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert einer ComboBox ändert.
     * @param selectedItem Der ausgewählte Datensatz.
     */
    private onSelectionChanged = (selectedItem: any, formKey: string, valueKey: string, selectedValueKey: string) =>
    {
        if (formKey === "range_from")
        {
            apiTimes.getCompleteTime(selectedItem[valueKey] + ":00").then((data) =>
            {
                this.setState((state: IState) =>
                {
                    state.rangeTo = data;
                    state.form.range_to = {};
                    state.form.range_from = {
                        value: selectedItem ? selectedItem[valueKey] : null,
                        selectedValue: selectedItem ? selectedItem[selectedValueKey] : null
                    };
                    return state;
                });
            });
        }
        else if (formKey === "lock_from")
        {
            apiTimes.getCompleteTime(selectedItem[valueKey] + ":00").then((data) =>
            {
                this.setState((state: IState) =>
                {
                    state.lockTo = data;
                    state.form.lock_to = {};
                    state.form.lock_from = {
                        value: selectedItem ? selectedItem[valueKey] : null,
                        selectedValue: selectedItem ? selectedItem[selectedValueKey] : null
                    };
                    return state;
                });
            });
        }
        else
        {
            this.setState((state: IState) =>
            {
                state.form[formKey] = {
                    value: selectedItem ? selectedItem[valueKey] : null,
                    selectedValue: selectedItem ? selectedItem[selectedValueKey] : null
                };
                return state;
            });
        }
    }

    /**
     * Wird ausgeführt, sobald sich der Wert einer CheckBox ändert.
     * @param e Das auslösende Ereignis.
     */
    private onCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>, formKey: string) =>
    {
        var target = e.target;
        var value = target.checked;

        this.setState((state: IState) =>
        {
            state.form[formKey] = value;
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald eine Sperre hinzugefügt werden soll.
     */
    private onAddLock = () =>
    {
        this.setState((state: IState) =>
        {
            if (state.form.locks != null)
            {
                state.form.locks.push({
                    weekday: state.form.lock_weekday.selectedValue,
                    weekday_de: state.form.lock_weekday.value,
                    from: state.form.lock_from.selectedValue,
                    to: state.form.lock_to.selectedValue
                });

                state.form.lock_weekday = {};
                state.form.lock_from = {};
                state.form.lock_to = {};
            }

            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald eine Sperre entfernt werden soll.
     */
    private onRemoveLock = (lock: models.ILock) =>
    {
        this.setState((state: IState) =>
        {
            state.form.locks.remove(lock);
            return state;
        });
    }

    /**
     * Rendert das Formular/den Content Bereich.
     */
    private renderContent(): JSX.Element
    {
        const {
            mode,
            calendar,
            calendarGroups,
            cementeries,
            rangeFrom,
            rangeTo,
            weekDays,
            lockFrom,
            lockTo,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        return (
            <div className="settings_dialog_content">
                <ScrollViewer
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.auto}
                    style={{
                        padding: 10
                    }}>
                    <FormGroup>
                        <TextBox
                            style={{
                                marginBottom: 15
                            }}
                            label="Name"
                            value={form.calendarname}
                            size={InputSize.normal}
                            spellCheck={true}
                            autoFocus={true}
                            errorMessage={errors["CalendarName"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "calendarname")} />
                        <ColorPicker
                            label="Farbe"
                            color={form.color}
                            colors="calendars"
                            errorMessage={errors["Color"]}
                            hint="Die Standardfarbe des Kalenders. Diese Farbe wird nur dann angezeigt wenn Termine kein Label und kein Status vorweisen."
                            onColorClick={this.onColorChanged} />
                        <ComboBox
                            label="Kalendergruppe"
                            canRemoveSelection={false}
                            dataSource={calendarGroups}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            value={form.calendar_group.value}
                            selectedValue={form.calendar_group.selectedValue}
                            onSelectionChanged={(data) => this.onSelectionChanged(data, "calendar_group", "group_name", "id")}
                            valueMember="id"
                            displayMember="group_name"
                            size={InputSize.normal}
                            errorMessage={errors["CalendarGroupId"]}
                            hint="Wird einem Kalender ein Friedhof zugewiesen, können Grabarten speziell für diesen Kalender/Friedhof angelegt werden.">
                            {
                                (item: modelsGet.ICalendarGroup) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.group_name} />
                                    );
                                }
                            }
                        </ComboBox>
                        <ComboBox
                            label="Friedhof"
                            canRemoveSelection={true}
                            dataSource={cementeries}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            value={form.cementery.value}
                            selectedValue={form.cementery.selectedValue}
                            onSelectionChanged={(data) => this.onSelectionChanged(data, "cementery", "name_long", "id")}
                            valueMember="id"
                            displayMember="name_long"
                            size={InputSize.normal}
                            errorMessage={errors["CementeryId"]}>
                            {
                                (item: modelsGet.ICementery) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.name_long} />
                                    );
                                }
                            }
                        </ComboBox>
                        <TextBox
                            label="Sortierung"
                            value={form.order_nr && form.order_nr.toString()}
                            valueType={InputValueType.number}
                            mask={InputMask.number}
                            size={InputSize.normal}
                            errorMessage={errors["OrderNr"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "order_nr")} />
                        <TextArea
                            label="Adressinformationen"
                            value={form.information}
                            spellCheck={true}
                            rows={6}
                            errorMessage={errors["Information"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "information")} />
                    </FormGroup>
                    <GroupBox
                        headerText="Darstellung/Ansicht"
                        style={{
                            marginTop: 30
                        }}>
                        <FormGroup>
                            <TextBox
                                label="Standard Zeitskala (Ansicht)"
                                value={form.timescale_view && form.timescale_view.toString()}
                                valueType={InputValueType.number}
                                mask={InputMask.number}
                                size={InputSize.normal}
                                errorMessage={errors["TimescaleView"]}
                                hint={<span>Ändert die Standardansicht in den Tagen- oder Wochenansichten. Je kleiner die Zahl desto mehr Details werden angezeigt.<br />Mögliche Eingabe: 5, 10, 15, 30</span>}
                                onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "timescale_view")} />
                        </FormGroup>
                        <FormGroup isHorizontal>
                            <ComboBox
                                label="Zeitspanne von"
                                canRemoveSelection={false}
                                dataSource={rangeFrom}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.range_from.value}
                                selectedValue={form.range_from.selectedValue}
                                onSelectionChanged={(data) => this.onSelectionChanged(data, "range_from", "short", "short")}
                                valueMember="short"
                                displayMember="short"
                                size={InputSize.normal}
                                errorMessage={errors["RangeFrom"]}>
                                {
                                    (item: modelsGet.ITime) =>
                                    {
                                        return (
                                            <DropDownTextItem
                                                key={item.short}
                                                value={item.short}
                                                displayValue={item.short} />
                                        );
                                    }
                                }
                            </ComboBox>
                            <ComboBox
                                label="Zeitspanne bis"
                                canRemoveSelection={false}
                                dataSource={rangeTo}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.range_to.value}
                                selectedValue={form.range_to.selectedValue}
                                onSelectionChanged={(data) => this.onSelectionChanged(data, "range_to", "short", "short")}
                                valueMember="short"
                                displayMember="short"
                                size={InputSize.normal}
                                errorMessage={errors["RangeTo"]}>
                                {
                                    (item: modelsGet.ITime) =>
                                    {
                                        return (
                                            <DropDownTextItem
                                                key={item.short}
                                                value={item.short}
                                                displayValue={item.short} />
                                        );
                                    }
                                }
                            </ComboBox>
                        </FormGroup>
                        <FormGroup>
                            <TextBox
                                label="Standard Termin Interval"
                                value={form.default_interval && form.default_interval.toString()}
                                valueType={InputValueType.number}
                                mask={InputMask.number}
                                size={InputSize.normal}
                                errorMessage={errors["DefaultInterval"]}
                                hint={<span>Hier die Zeitspanne eintragen in der Termine angelegt werden müssen. Zum Beispiel <strong>30</strong> um 30 minütige Termine zu erzwingen.</span>}
                                onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "default_interval")} />
                        </FormGroup>
                    </GroupBox>
                    <FormGroup>
                        <CheckBox
                            style={{
                                marginTop: 15
                            }}
                            label="Freie Terminzeiträume via Blöcke darstellen"
                            isChecked={form.show_free_blocks}
                            onCheckedChanged={(e) => this.onCheckboxChanged(e, "show_free_blocks")} />
                        <CheckBox
                            style={{
                                marginLeft: 28
                            }}
                            label="Lückenlose Terminvergabe erzwingen"
                            isChecked={form.complete_appointment}
                            isDisabled={!form.show_free_blocks}
                            onCheckedChanged={(e) => this.onCheckboxChanged(e, "complete_appointment")} />
                    </FormGroup>
                    <GroupBox
                        headerText="Rüstzeit"
                        style={{
                            marginTop: 30
                        }}>
                        <FormGroup>
                            <CheckBox
                                label="Rüstzeit automatisch bis zum nächsten Termin auffüllen."
                                isChecked={form.automatic_setuptime}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "automatic_setuptime")} />
                            <TextBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Standard Rüstzeit für die automatische Berechnung"
                                value={form.setuptime && form.setuptime.toString()}
                                valueType={InputValueType.number}
                                mask={InputMask.number}
                                size={InputSize.normal}
                                isDisabled={!form.automatic_setuptime}
                                errorMessage={errors["Setuptime"]}
                                onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "setuptime")} />
                        </FormGroup>
                    </GroupBox>
                    <GroupBox
                        headerText="Sperren"
                        style={{
                            marginTop: 30
                        }}>
                        <FormGroup isHorizontal>
                            <ComboBox
                                label="Wochentag"
                                canRemoveSelection={true}
                                dataSource={weekDays}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.lock_weekday.value}
                                selectedValue={form.lock_weekday.selectedValue}
                                onSelectionChanged={(data) => this.onSelectionChanged(data, "lock_weekday", "value", "value_en")}
                                valueMember="value_en"
                                displayMember="value"
                                size={InputSize.normal}>
                                {
                                    (item: models.IWeekDay) =>
                                    {
                                        return (
                                            <DropDownTextItem
                                                key={item.key}
                                                value={item.key}
                                                displayValue={item.value} />
                                        );
                                    }
                                }
                            </ComboBox>
                            <ComboBox
                                label="Von"
                                canRemoveSelection={true}
                                dataSource={lockFrom}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.lock_from.value}
                                selectedValue={form.lock_from.selectedValue}
                                onSelectionChanged={(data) => this.onSelectionChanged(data, "lock_from", "short", "short")}
                                valueMember="short"
                                displayMember="short"
                                size={InputSize.normal}>
                                {
                                    (item: modelsGet.ITime) =>
                                    {
                                        return (
                                            <DropDownTextItem
                                                key={item.short}
                                                value={item.short}
                                                displayValue={item.short} />
                                        );
                                    }
                                }
                            </ComboBox>
                            <ComboBox
                                label="Bis"
                                canRemoveSelection={true}
                                dataSource={lockTo}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.lock_to.value}
                                selectedValue={form.lock_to.selectedValue}
                                onSelectionChanged={(data) => this.onSelectionChanged(data, "lock_to", "short", "short")}
                                valueMember="short"
                                displayMember="short"
                                size={InputSize.normal}>
                                {
                                    (item: modelsGet.ITime) =>
                                    {
                                        return (
                                            <DropDownTextItem
                                                key={item.short}
                                                value={item.short}
                                                displayValue={item.short} />
                                        );
                                    }
                                }
                            </ComboBox>
                        </FormGroup>
                        <FormGroup>
                            <Button
                                style={{
                                    marginBottom: 10
                                }}
                                text="Sperre hinzufügen"
                                type={ButtonType.default}
                                isDisabled={form.lock_weekday.selectedValue == null || form.lock_from.selectedValue == null || form.lock_to.selectedValue == null}
                                onClick={this.onAddLock} />
                            {
                                form.locks && form.locks.length > 0 &&
                                <div className="locks_items">
                                    {
                                        form.locks.map((lock) =>
                                        {
                                            let from: Time = Time.parse(lock.from);
                                            let to: Time = Time.parse(lock.to);

                                            return (
                                                <div className="locks_items_item">
                                                    <span className="locks_items_item_text">
                                                        {lock.weekday_de + " von " + from.toShortTimeString() + " Uhr bis " + to.toShortTimeString() + " Uhr"}
                                                    </span>
                                                    <a
                                                        className="locks_items_item_remove"
                                                        onClick={() => this.onRemoveLock(lock)}>Entfernen</a>
                                                </div>
                                            );
                                        })
                                    }
                                </div>
                            }
                            {
                                form.locks == null || form.locks.length === 0 &&
                                <div className="locks_no-items">
                                    Es sind keine Sperren eingerichtet
                                </div>
                            }
                        </FormGroup>
                    </GroupBox>
                </ScrollViewer>
            </div>
        );
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const {
            isLoading,
            mode,
            calendar,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        var icon: string = "loading";
        var title: string = "Daten werden geladen...";
        var buttonHeader: string = "";

        if (!isLoading)
        {
            if (mode === Mode.create)
            {
                icon = "plus";
                title = "Neuen Kalender erstellen";
                buttonHeader = "Erstellen";
            }
            else
            {
                icon = "pencil";
                title = `Kalender "${calendar.name}" bearbeiten`;
                buttonHeader = "Speichern";
            }
        }

        return (
            <Window
                isOpen={true}
                titleIcon={icon}
                title={title}
                onClosing={this.onClosing}
                isMaximized={false}
                width={600}
                height={700}
                onEnterKeyUp={this.onSaveOrCreateClick}
                onEscapeKeyUp={() => this.onClosing(false)}>
                <div className="settings_dialog">
                    <Alert
                        style={utils.getGridChildStyle(1, 1, 1, 1)}
                        alertType={alertType}
                        isVisible={alertMessage ? true : false}
                        isClosable={true}
                        onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                        {alertMessage}
                    </Alert>
                    {isLoading ? <Loader loaderStyle={LoaderStyle.style1} /> : this.renderContent()}
                    <div className="settings_dialog_actions">
                        {
                            mode === Mode.edit &&
                            <Button
                                text="Löschen"
                                type={ButtonType.danger}
                                onClick={this.onDeleteClick} />
                        }
                        <div style={{ flexGrow: 1 }}></div>
                        <Button
                            text={buttonHeader}
                            type={ButtonType.primary}
                            isDisabled={this._isSaveButtonDisabled}
                            onClick={this.onSaveOrCreateClick} />
                        <Button
                            text="Abbrechen"
                            type={ButtonType.default}
                            onClick={() => this.onClosing(false)} />
                    </div>
                </div>
            </Window>
        );
    }
}